import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import ReligiousEducationDetails from './ReligiousEducationDetails';
import ReligiousEducationSearch from './ReligiousEducationSearch';
import ReligiousEducationForm from './ReligiousEducationForm';
import { useError } from '../contexts/ErrorContext';
import { useLoading } from '../contexts/LoadingContext';
import { useAuth } from '../contexts/AuthContext';

function formatPhoneNumber(phone) {
  // Remove all non-digit characters
  const cleaned = ('' + phone).replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return phone; // fallback to original if not 10 digits
}

const ReligiousEducationList = () => {
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [sortField, setSortField] = useState('registrationDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');
  const [availableSchoolYears, setAvailableSchoolYears] = useState([]);
  const [currentSchoolYear, setCurrentSchoolYear] = useState('');
  const { showError } = useError();
  const { showLoading, hideLoading } = useLoading();
  const { user } = useAuth();

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (selectedSchoolYear) {
      fetchRegistrations(selectedSchoolYear);
    }
  }, [selectedSchoolYear]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/religious-education/settings');
      if (response.data) {
        const years = response.data.availableSchoolYears || [];
        const current = response.data.currentSchoolYear || '';
        setAvailableSchoolYears(years);
        setCurrentSchoolYear(current);
        // Default to current school year
        setSelectedSchoolYear(current || (years.length > 0 ? years[0] : ''));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Still try to fetch all registrations if settings fail
      fetchRegistrations('');
    }
  };

  const fetchRegistrations = async (schoolYear) => {
    try {
      const params = {};
      if (schoolYear) {
        params.schoolYear = schoolYear;
      }
      const response = await axios.get('/religious-education', { params });
      if (response.data) {
        console.log(`Number of registrations: ${response.data.length}`);
        setRegistrations(response.data);
      }
    } catch (error) {
      setRegistrations([]);
      showError('Failed to fetch registrations');
    }
  };

  const handleSchoolYearChange = (event) => {
    setSelectedSchoolYear(event.target.value);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (filter) => {
    // Implement filtering logic based on payment status
    const filtered = registrations.filter(registration => {
      switch (filter) {
        case 'paid':
          return registration.payment.status === 'paid';
        case 'hardship':
          return registration.payment.status === 'hardship';
        default:
          return true;
      }
    });
    setRegistrations(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredRegistrations = registrations.filter(registration => {
    const searchLower = searchTerm.toLowerCase();
    // Check family name, contact name, email, phone
    const matchesBasic = (
      registration.family.name.toLowerCase().includes(searchLower) ||
      registration.family.primaryContact.name.toLowerCase().includes(searchLower) ||
      registration.family.primaryContact.email.toLowerCase().includes(searchLower) ||
      registration.family.primaryContact.phone.includes(searchTerm)
    );
    // Also check children's names
    const matchesChild = registration.children?.some(child =>
      child.name?.toLowerCase().includes(searchLower)
    );
    return matchesBasic || matchesChild;
  });

  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    let aValue = null, bValue = null;
    
    // Get nested values safely
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((current, key) => (current ? current[key] : null), obj);
    };
    
    switch (sortField) {
      case 'family.name':
        aValue = getNestedValue(a, 'family.name');
        bValue = getNestedValue(b, 'family.name');
        break;
      case 'family.primaryContact.name':
        aValue = getNestedValue(a, 'family.primaryContact.name');
        bValue = getNestedValue(b, 'family.primaryContact.name');
        break;
      case 'family.primaryContact.email':
        aValue = getNestedValue(a, 'family.primaryContact.email');
        bValue = getNestedValue(b, 'family.primaryContact.email');
        break;
      case 'family.primaryContact.phone':
        aValue = getNestedValue(a, 'family.primaryContact.phone');
        bValue = getNestedValue(b, 'family.primaryContact.phone');
        break;
      case 'children':
        aValue = a.children ? a.children.length : 0;
        bValue = b.children ? b.children.length : 0;
        break;
      case 'registrationDate':
        aValue = a.registrationDate ? new Date(a.registrationDate) : new Date(0);
        bValue = b.registrationDate ? new Date(b.registrationDate) : new Date(0);
        break;
      default:
        aValue = a[sortField];
        bValue = b[sortField];
    }

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom component="div" sx={{ mb: 0 }}>
          Religious Education Registrations
        </Typography>
        {user && user.isAdmin && (
          <IconButton component={Link} to="/religious-education/settings" color="primary" aria-label="settings">
            <SettingsIcon />
          </IconButton>
        )}
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel id="school-year-label">School Year</InputLabel>
              <Select
                labelId="school-year-label"
                id="school-year-select"
                value={selectedSchoolYear}
                label="School Year"
                onChange={handleSchoolYearChange}
              >
                {availableSchoolYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}{year === currentSchoolYear ? ' (Current)' : ''}
                  </MenuItem>
                ))}
                <MenuItem value="">All Years</MenuItem>
              </Select>
            </FormControl>
            <ReligiousEducationSearch onSearch={handleSearch} onFilter={handleFilter} />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsFormOpen(true)}
          >
            New Manual Registration
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Family Name</TableCell>
                <TableCell>Primary Contact</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Children</TableCell>
                <TableCell>Registration Date</TableCell>
                <TableCell>Payment Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRegistrations.map((registration) => (
                <TableRow
                  key={registration._id}
                  onClick={() => setSelectedRegistration(registration)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell>{registration.family.name}</TableCell>
                  <TableCell>{registration.family.primaryContact.name}</TableCell>
                  <TableCell>{registration.family.primaryContact.email}</TableCell>
                  <TableCell>{registration.family.primaryContact.phone ? formatPhoneNumber(registration.family.primaryContact.phone) : ''}</TableCell>
                  <TableCell>{registration.children.length}</TableCell>
                  <TableCell>{new Date(registration.registrationDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={registration.payment?.status ? registration.payment.status.charAt(0).toUpperCase() + registration.payment.status.slice(1) : 'Open'}
                      color={
                        registration.payment?.status === 'paid' ? 'success' :
                        registration.payment?.status === 'hardship' ? 'info' :
                        'warning'
                      }
                      variant="filled"
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedRegistration && (
          <ReligiousEducationDetails
            registration={selectedRegistration}
            open={Boolean(selectedRegistration)}
            onClose={() => setSelectedRegistration(null)}
            onUpdate={fetchRegistrations}
          />
        )}

        <ReligiousEducationForm
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => fetchRegistrations(selectedSchoolYear)}
          schoolYear={selectedSchoolYear}
          currentSchoolYear={currentSchoolYear}
        />
      </Paper>
    </Container>
  );
};

export default ReligiousEducationList; 